import type { JSX } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { FK, panel } from './tokens';

export interface PageTitleProps {
  /** Название страницы — h1 30/800, трекинг −0,5 (как `.fk-page-title`). */
  title: ReactNode;
  /** Подзаголовок: что на странице, за какой период. */
  sub?: ReactNode;
  /** Строка над заголовком (хлебные крошки «База знаний / Кухня»). */
  crumbs?: ReactNode;
  /** Кнопки справа (ExportMenu, «Создать»…), переносятся под заголовок на узком экране. */
  right?: ReactNode;
  style?: CSSProperties;
}

/** Заголовок страницы HUB 2.0 (порт CRM `ds/PageTitle`, размер — вики 30/800). */
export function PageTitle({ title, sub, crumbs, right, style }: PageTitleProps): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 20, ...style }}>
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        {crumbs ? <div style={{ fontSize: 13, color: FK.muted, marginBottom: 6 }}>{crumbs}</div> : null}
        <h1 className="fk-page-title" style={{ lineHeight: 1.2 }}>{title}</h1>
        {sub ? <div className="fk-page-sub">{sub}</div> : null}
      </div>
      {right ? <div className="fk-ds-noprint" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{right}</div> : null}
    </div>
  );
}

/** Шапка карточки: заголовок 15/700, подпись, слот справа; тонкая линия снизу. */
export function PanelHead({ title, sub, icon, right }: { title: ReactNode; sub?: ReactNode; icon?: ReactNode; right?: ReactNode }): JSX.Element {
  return (
    <div
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${FK.rowLine}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {icon ? <span style={{ color: FK.orange, fontSize: 16, lineHeight: 1 }}>{icon}</span> : null}
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <b style={{ fontSize: 15, color: FK.text }}>{title}</b>
          {sub ? <span style={{ fontSize: 12, color: FK.text2 }}>{sub}</span> : null}
        </span>
      </span>
      {right}
    </div>
  );
}

export interface PanelProps {
  /** Заголовок карточки — рисуется `PanelHead`. */
  title?: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  right?: ReactNode;
  /** Внутренний отступ тела (по умолчанию 16; 0 — для таблиц/списков во всю ширину). */
  pad?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/** Карточка-панель: белая, граница 1px, радиус 8, без цветной левой полосы. */
export function Panel({ title, sub, icon, right, pad = 16, className, style, children }: PanelProps): JSX.Element {
  return (
    <section className={className} style={{ ...panel, overflow: 'hidden', minWidth: 0, ...style }}>
      {title ? <PanelHead title={title} sub={sub} icon={icon} right={right} /> : null}
      {children != null ? <div style={{ padding: pad }}>{children}</div> : null}
    </section>
  );
}
